// Llama caravans + their decoration (M61). PURE (ENG-1).
//
// A leashed llama is not just tethered — it becomes a link in a CARAVAN. Lead one
// llama and every llama already following it comes along, which is the whole
// reason to own more than one, and the reason a wandering trader arrives with a
// pair in tow.
//
// The rules that matter, all Minecraft's: a llama follows exactly one leader, a
// leader tows at most one follower directly (the train is a chain, never a fan),
// a chain has a maximum length, and no link may close a loop. Everything here is
// arithmetic over ids so the chain can be reasoned about without a world.
import { CARAVAN } from '../config';

/** Who is following whom: follower entity id → leader entity id. */
export type CaravanLinks = ReadonlyMap<number, number>;

/** Only llamas caravan. Anything else on a lead is simply tethered. */
export function caravans(type: string): boolean {
  return type === 'llama' || type === 'trader_llama';
}

/** The chain from `id` up to its head, nearest leader first. Stops at the length
 *  cap so a corrupt (looping) link set can never spin here. */
export function chainAbove(links: CaravanLinks, id: number): number[] {
  const out: number[] = [];
  let cur = links.get(id);
  while (cur !== undefined && out.length < CARAVAN.MAX_LENGTH + 1) {
    if (out.includes(cur) || cur === id) break; // a loop: stop rather than spin
    out.push(cur);
    cur = links.get(cur);
  }
  return out;
}

/** Everything trailing behind `id`, front to back. */
export function chainBelow(links: CaravanLinks, id: number): number[] {
  const followerOf = new Map<number, number>();
  for (const [follower, leader] of links) followerOf.set(leader, follower);
  const out: number[] = [];
  let cur = followerOf.get(id);
  while (cur !== undefined && out.length < CARAVAN.MAX_LENGTH + 1) {
    if (out.includes(cur) || cur === id) break;
    out.push(cur);
    cur = followerOf.get(cur);
  }
  return out;
}

export type JoinRefusal = 'not-a-llama' | 'self' | 'already-following' | 'leader-taken' | 'loop' | 'too-long';

/**
 * May `follower` be hitched behind `leader`? Returns null when it may, or the
 * reason it may not — the host prints one and refuses rather than silently
 * doing nothing.
 */
export function refuseJoin(
  links: CaravanLinks,
  followerType: string,
  followerId: number,
  leaderId: number,
): JoinRefusal | null {
  if (!caravans(followerType)) return 'not-a-llama';
  if (followerId === leaderId) return 'self';
  if (links.has(followerId)) return 'already-following';
  for (const l of links.values()) if (l === leaderId) return 'leader-taken';
  // A loop would be a caravan chasing its own tail forever.
  if (chainAbove(links, leaderId).includes(followerId)) return 'loop';
  const length = chainAbove(links, leaderId).length + 1 + chainBelow(links, followerId).length + 1;
  if (length > CARAVAN.MAX_LENGTH) return 'too-long';
  return null;
}

/** Hitch `follower` behind `leader`, returning the new link set (or the old one
 *  unchanged when the join is refused). */
export function join(links: CaravanLinks, followerType: string, followerId: number, leaderId: number): Map<number, number> {
  const next = new Map(links);
  if (refuseJoin(links, followerType, followerId, leaderId) === null) next.set(followerId, leaderId);
  return next;
}

/** Unhitch one llama. Whoever was behind it is unhitched too — Minecraft breaks
 *  the caravan at the cut rather than splicing it back together. */
export function leave(links: CaravanLinks, id: number): Map<number, number> {
  const next = new Map(links);
  next.delete(id);
  for (const [follower, leader] of links) if (leader === id) next.delete(follower);
  return next;
}

/**
 * Where a follower wants to be: trailing its leader at the tether distance, on
 * the line back from the leader toward where the follower already is. Returns
 * null when it is close enough to just graze — a caravan that never stops
 * correcting reads as a shove, not a walk.
 */
export function followPoint(
  leaderX: number, leaderZ: number,
  selfX: number, selfZ: number,
): { x: number; z: number } | null {
  const dx = selfX - leaderX, dz = selfZ - leaderZ;
  const d = Math.hypot(dx, dz);
  if (d <= CARAVAN.FOLLOW_DISTANCE) return null;
  const k = CARAVAN.FOLLOW_DISTANCE / d;
  return { x: leaderX + dx * k, z: leaderZ + dz * k };
}

/** A tether this long has snapped: the follower is unhitched (Minecraft's rule —
 *  a lead breaks rather than dragging a llama through a wall). */
export function tetherSnapped(leaderX: number, leaderZ: number, selfX: number, selfZ: number): boolean {
  return Math.hypot(selfX - leaderX, selfZ - leaderZ) > CARAVAN.SNAP_DISTANCE;
}

// ── Decoration ───────────────────────────────────────────────────────────────
/** The dye colour a carpet item names, or null if it isn't a coloured carpet.
 *  (`moss_carpet` is a block, not a llama blanket.) */
export function carpetColour(item: string, colours: readonly string[]): string | null {
  if (!item.endsWith('_carpet')) return null;
  const colour = item.slice(0, -'_carpet'.length);
  return colours.includes(colour) ? colour : null;
}

/** Does right-clicking with `held` dress this llama? A llama wears one carpet;
 *  swapping it for another is not a thing, as in Minecraft — you shear it off. */
export function dressesLlama(type: string, decorated: boolean, baby: boolean, colour: string | null): boolean {
  return caravans(type) && !decorated && !baby && colour !== null;
}

/** What a decorated llama gives back when sheared / on death: its carpet. */
export function llamaDecorDrop(colour: string | null): readonly string[] {
  return colour ? [`${colour}_carpet`] : [];
}
